import React, { useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import { Button, ButtonGroup, CardTitle, UncontrolledTooltip } from 'reactstrap';

import { TableList } from './documents-tab/TableList';
import { UploadDocument } from './documents-tab/UploadDocument';
import { SelectAttributes } from './documents-tab/SelectAttributes';
import { ConfirmUpload } from './documents-tab/ConfirmUpload';
import toast from 'react-hot-toast';

import { setting, member } from '../../../../api';
import UserContext from '../../../UserContext';
import { InternalServerErrorException } from '../../../../api/exceptions/InternalServerErrorException';
import { InvalidParametersException } from '../../../../api/exceptions/InvalidParametersException';
import { SettingsClearancesType } from 'api/settings/clearances';
import { FileWithAttributes } from 'api/tempTypes';
import { MemberDB } from '../../../../api/members';
import { FailedToFetchException } from 'api/exceptions/FailedToFetchException';

interface Props {
	uid: string;
	closeModalControl: (state: boolean) => void;
}

export const DocumentsTab = (props: Props) => {
	const { uid, closeModalControl } = props;
	const { jwt } = useContext(UserContext);

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isTableList, setIsTableList] = useState(true);
	const [isUploadDocument, setIsUploadDocument] = useState(false);
	const [viewAllDocuments, setViewAllDocuments] = useState(false);
	const [clearanceSettings, setClearanceSettings] = useState<Array<SettingsClearancesType>>([]);
	const [memberData, setMemberData] = useState<MemberDB>();
	const [uploadState, setUploadState] = useState(1);
	const [uploadedFiles, setUploadedFiles] = useState<Array<FileWithAttributes>>([]);

	useEffect(() => {
		Promise.resolve()
			.then(() => {
				// Retreive the dynamic fields from the backend
				return setting.clearances
					.get(jwt)
					.then((settings) => {
						setClearanceSettings(settings.types_list);
					})
					.catch((e) => {
						// TODO: Handle better
						console.error('error');
						console.error(e);
						Sentry.captureException(e);
					});
			})
			.then(() => {
				return member
					.get(jwt, uid)
					.then((member) => {
						setMemberData(member);
					})
					.catch((e) => {
						// TODO: Handle better
						console.error('error');
						console.error(e);
						Sentry.captureException(e);
					});
			});
	}, []);

	const toggleViewAllDocumentsClick = () => {
		setViewAllDocuments(!viewAllDocuments);
	};

	const handleAddButtonClick = () => {
		setIsTableList(false);
		setIsUploadDocument(true);
		closeModalControl(true);
	};

	const handleAddButtonClose = () => {
		setIsTableList(true);
		setIsUploadDocument(false);
		closeModalControl(false);
	};

	const handleUploadDocument = (acceptedFiles: Array<FileWithAttributes>) => {
		if (acceptedFiles instanceof Array) {
			const file = acceptedFiles[0];
			file.attributes = {};
			uploadedFiles.push(file);

			setUploadState(2);
			setUploadedFiles(uploadedFiles);
		}
	};

	const handleAttributeNextStepClick = () => {
		setUploadState(3);
	};

	const handleAttributeSelect = (fileIndex: number, attribute: string, value: any) => {
		let tmp = uploadedFiles;

		tmp[fileIndex].attributes[attribute] = value;

		setUploadedFiles(tmp);
	};

	const handleConfirmUploadStepClick = async () => {
		const formData = new FormData();
		let attributes: any = [];
		uploadedFiles.forEach((file) => {
			let obj: any = {};
			obj[file.name] = file.attributes;

			attributes.push(obj);
		});

		formData.append('file-attributes', JSON.stringify(attributes));
		formData.append('files', uploadedFiles[0]);

		try {
			await member.document.post(jwt, uid, formData);

			setUploadState(1);
			setUploadedFiles([]);
			setIsTableList(true);
			setIsUploadDocument(false);
			closeModalControl(false);
		} catch (error) {
			if (error instanceof InvalidParametersException) {
				toast.error(error.message);
			} else if (error instanceof InternalServerErrorException) {
				toast.error('Failed to upload. Please try again.');
				console.error(error);
				Sentry.captureException(error);
			} else if (error instanceof FailedToFetchException) {
				toast.error(error.message);
			} else {
				toast.error('There was an error uploading.');
				console.error('error');
				console.error(error);
				Sentry.captureException(error);
			}
		}
	};

	return (
		<div className="documents-tab">
			{!isUploadDocument ? (
				<ButtonGroup className="pull-right">
					<Button
						color="info"
						size="sm"
						className="btn-icon"
						id={`member-details-documents-tab-add-doc-button`}
						onClick={() => handleAddButtonClick()}
						disabled={memberData?.archived !== null}
					>
						<i className="fa fa-plus" />
					</Button>
					<Button
						color="secondary"
						size="sm"
						className="btn-icon"
						id={`member-details-documents-tab-view-all-docs-button`}
						onClick={() => toggleViewAllDocumentsClick()}
					>
						<i className={`fa fa-list-ul`} />
					</Button>
					<UncontrolledTooltip delay={0} target={`member-details-documents-tab-add-doc-button`}>
						{memberData?.archived !== null ? <>Member is archived</> : <>Add New Document</>}
					</UncontrolledTooltip>
					<UncontrolledTooltip delay={0} target={`member-details-documents-tab-view-all-docs-button`}>
						View All Documents
					</UncontrolledTooltip>
				</ButtonGroup>
			) : (
				<></>
			)}
			<CardTitle tag="h4" style={{ marginTop: 0, marginBottom: '.5rem' }}>
				Documents {isUploadDocument ? '> Upload' : <></>}
			</CardTitle>
			{isTableList ? (
				<TableList key={uid} uid={uid} clearanceSettings={clearanceSettings} viewAllDocuments={viewAllDocuments} />
			) : isUploadDocument ? (
				<>
					{uploadState === 1 ? (
						<UploadDocument
							key={uid}
							onBackButtonClick={() => handleAddButtonClose()}
							onDrop={(acceptedFiles) => handleUploadDocument(acceptedFiles)}
							validFileTypes={'image/*'}
						/>
					) : uploadState === 2 ? (
						<SelectAttributes
							files={uploadedFiles}
							onNextStepClick={() => handleAttributeNextStepClick()}
							onAttributeSelect={handleAttributeSelect}
							clearanceSettings={clearanceSettings}
						/>
					) : uploadState === 3 ? (
						<ConfirmUpload clearanceSettings={clearanceSettings} files={uploadedFiles} onConfirm={() => handleConfirmUploadStepClick()} />
					) : (
						<span>???</span>
					)}
				</>
			) : (
				<></>
			)}
		</div>
	);
};
